import { Component, Inject, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { ActivityLoggerService } from 'src/app/_shared/service/activity-logger.service';
import constant from 'src/constant.json';
import { LoanComputationService } from '../../service/loan-computation.service';
import { MyHomeService } from '../../service/my-home.service';
import { MyHomeState } from '../../state/my-home.state';

interface MortgageDialogContent {
  type: string;
  title: string;
  message: string;
  principal: string;
  interest: number;
  term: number;
  startDate: Date;
  id: string;
  index: number;
}

@Component({
  selector: 'app-create-mortgage-dialog',
  templateUrl: './create-mortgage-dialog.component.html',
  styleUrls: ['./create-mortgage-dialog.component.scss'],
})
export class CreateMortgageDialogComponent implements OnInit {
  createMortgageForm: FormGroup;
  today = new Date();
  mortgageCreationInProgress: boolean = false;
  onDestroyNotifier$ = new Subject();
  loggerAddress: any;
  pmxPropertyId;
  isEquityAnalysis: boolean;
  editableMorgageList: any;

  constructor(
    public dialogRef: MatDialogRef<CreateMortgageDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: MortgageDialogContent,
    private formBuilder: FormBuilder,
    private myHomeService: MyHomeService,
    private myHomeState: MyHomeState,
    private activityLoggerService: ActivityLoggerService,
    private route: ActivatedRoute,
    private router: Router,
    private loanService: LoanComputationService,
  ) { }

  ngOnInit() {
    const path = this.router.url.split('?')[0];
    if (path?.includes('equity-analysis')) {
      this.isEquityAnalysis = true;
    }
    this.myHomeState.activePropertyDetails$.pipe(takeUntil(this.onDestroyNotifier$)).subscribe((res: any) => {
      if (res && res.basic) {
        this.pmxPropertyId = res.basic.PMXPropertyId;
        this.loggerAddress = {
          city: res.basic.City,
          state: res.basic.StateOrProvince,
          street: res.basic.StreetAddress,
          zip: res.basic.PostalCode,
        };
      }
    });
    const propertyId = this.route.snapshot.queryParamMap.get('id');
    let propertyPurchaseValue;
    if (propertyId) {
      this.myHomeState.myHomeList$.pipe(take(1)).subscribe((homeList) => {
        const currentPropertyData = homeList.find((data) => {
          return data.id == propertyId;
        })
        propertyPurchaseValue = currentPropertyData?.purchaseValue;
      });
    }
    this.createMortgageForm = this.formBuilder.group({
      principal: [this.data.principal || null, [Validators.required, Validators.min(100), PrincipalValidator(propertyPurchaseValue)]],
      interest: [this.data.interest || '', [Validators.required, Validators.min(1), Validators.max(20)]],
      term: [this.data.term || '', [Validators.required, Validators.min(1), Validators.max(49)]],
      startDate: [new Date(this.data.startDate) || '', Validators.required],
    });


  }

  get createMortgageFormValidation() {
    return this.createMortgageForm.controls;
  }
  setInitialProjection() {


    let loanComputation = this.loanService.computeTotalLoan(
      this.createMortgageForm.value.principal,
      this.createMortgageForm.value.interest / 100,
      this.createMortgageForm.value.term * 12,
      new Date(this.createMortgageForm.value.startDate)
    );


    let activeRefinance = this.loanService.computeBalanceLoan(
      loanComputation.principalBalance,
      this.createMortgageForm.value.interest / 100,
      15 * 12
    );

    this.createMortgageForm.value.term = this.createMortgageForm.value.term * 12;
    let result = {
      ...this.createMortgageForm.value,
      ...loanComputation,
    };
    result['activeRefinance'] = activeRefinance;
    result['temp_id'] = Date.now() + Math.floor(Math.random() * 1000);
    return result;


  }

  saveMortgage() {
    if (!this.createMortgageForm.valid) return false;

    if (this.isEquityAnalysis) {
      let status = true;
      if (this.data.type === 'addMortgage') {

        this.createMortgageForm.value.startDate = this.createMortgageForm.value.startDate.toISOString();
        this.createMortgageForm.value.homeId = null;
        this.createMortgageForm.value.lender = '';
        this.createMortgageForm.value.monthlyPayment = 0;
        this.createMortgageForm.value.createType = 'new';
        this.createMortgageForm.value.refinance = {};
        this.createMortgageForm.value.extraPayment = 0;
        this.createMortgageForm.value.refinanceScenarioList = [
          {
            interestRate: this.createMortgageForm.value.interest,
            //  termBalance: this.createMortgageForm.value.term,
            termBalance: 180, // default 15 Years
            closingCost: 0,
            name: 'New Loan Scenario 1',
            isActive: false,
            id: 1,
          },
        ];
        this.myHomeState.mortgageListValue = [
          ...(this.myHomeState.mortgageListValue || []),
          this.setInitialProjection()
        ];
        this.proceedCancelAction();
        return

      }
      if (this.data.type === 'editMortgage') {
        status = false;
        this.myHomeState.mortgageList$.pipe(takeUntil(this.onDestroyNotifier$)).subscribe((mortgageList) => {
          this.editableMorgageList = mortgageList;
        });

        if (
          this.editableMorgageList &&
          this.data.index !== null &&
          this.data.index !== undefined
        ) {
          const finalMortgageList = [...this.editableMorgageList];

          finalMortgageList[this.data.index] = this.setInitialProjection();

          this.myHomeState.mortgageListValue = finalMortgageList;
        }
        this.dialogRef.close();
        return
      }


    }

    let myHomeValues = this.myHomeState.myHomeListValue.filter((myHome) => {
      return myHome.id == this.myHomeState.activeHomeValue.id;
    });
    myHomeValues = myHomeValues[0];
    myHomeValues.homeId = myHomeValues.id;
    this.createMortgageForm.value.startDate = this.createMortgageForm.value.startDate.toISOString();
    let status = true;
    if (this.data.type === 'addMortgage') {
      status = true;
      this.createMortgageForm.value.homeId = myHomeValues.id;
      this.createMortgageForm.value.lender = '';
      this.createMortgageForm.value.monthlyPayment = 0;
      this.createMortgageForm.value.createType = 'new';
      this.createMortgageForm.value.refinance = {};
      this.createMortgageForm.value.extraPayment = 0;
      this.createMortgageForm.value.refinanceScenarioList = [
        {
          interestRate: this.createMortgageForm.value.interest,
          //  termBalance: this.createMortgageForm.value.term,
          termBalance: 180, // default 15 Years
          closingCost: 0,
          name: 'New Loan Scenario 1',
          isActive: false,
          id: 1,
        },
      ];
      if (myHomeValues.mortgageList) {
        myHomeValues.mortgageList.push(this.createMortgageForm.value);
      }
      this.logUserActivity(
        constant.LOGGER.myHome.category,
        constant.LOGGER.myHome.action.mtgRequestInit.key,
        this.pmxPropertyId,
        this.loggerAddress
      );
    } else if (this.data.type === 'editMortgage') {
      status = false;
      myHomeValues.mortgageList.map((mortgage) => {
        if (mortgage.id === this.data.id) {
          mortgage.principal = this.createMortgageForm.value.principal;
          mortgage.interest = this.createMortgageForm.value.interest;
          mortgage.term = this.createMortgageForm.value.term;
          mortgage.startDate = this.createMortgageForm.value.startDate;
        }
      });
      this.logUserActivity(
        constant.LOGGER.myHome.category,
        constant.LOGGER.myHome.action.mortgageDetailsUpdate.key,
        this.pmxPropertyId,
        this.loggerAddress
      );
    }
    this.myHomeService
      .updateMyHomeValues([myHomeValues], status)
      .pipe(takeUntil(this.onDestroyNotifier$))
      .subscribe((res: any) => {
        this.mortgageCreationInProgress = res.isLoading;
        if (!res.isLoading) {
          this.dialogRef.close();
        }
      });
  }

  logUserActivity(category, action, pmxPropertyId, address) {
    let loggerParams = {
      category: category,
      action: action,
    };
    let inputData = {
      pmxPropertyId: pmxPropertyId,
      address: address,
    };
    this.activityLoggerService.logActivity(loggerParams, inputData);
  }

  proceedCancelAction() {
    this.dialogRef.close();
  }
  removeAtIndex(index: number, data) {
    const result = [];
    for (let i = 0; i < data.length; i++) {
      if (i !== index) {
        result.push(data[i]);
      }
    }
    return result;
  }
  ngOnDestroy() {
    this.onDestroyNotifier$.next();
    this.onDestroyNotifier$.complete();
  }
}

export function PrincipalValidator(purchaseValue: any): ValidatorFn {
  return (control: AbstractControl) => {
    if (control.value != null && control.value > Number(purchaseValue)) {
      return { principalTooHigh: true };
    }
    return null;
  };
}